/** Permission required to open the Printing page (nav + route). */
export const VIEW_PRINTING = 'view_printing';

export const PRINTING_PAGE_PERMISSIONS = [VIEW_PRINTING];

/**
 * Print categories and the permission(s) required to print each.
 * Page access is separate (`view_printing`); each category still needs its view permission.
 * - permission: single required permission
 * - allPermissions: every listed permission required
 * - anyPermissions: at least one listed permission required
 */
export const PRINT_CATEGORIES = [
  {
    id: 'profile',
    label: 'Profile',
    description: 'Name, contact, hire/status',
    permission: 'view_employees'
  },
  {
    id: 'notes',
    label: 'Notes',
    description: 'Internal employee notes',
    permission: 'view_notes'
  },
  {
    id: 'disciplinary',
    label: 'Disciplinary',
    description: 'Actions and attachment filenames',
    permission: 'view_disciplinary_actions'
  },
  {
    id: 'documents',
    label: 'Documents',
    description: 'I-9, food handler/manager, W-4, expirations, card numbers',
    permission: 'view_documents'
  },
  {
    id: 'employee_file',
    label: 'Employee file',
    description:
      'Personnel file: application, evals, leave docs, injury reports, payroll uploads, employee requests, agreements; plus closed disciplinary and time off when permitted',
    anyPermissions: [
      'view_employee_file_application',
      'view_employee_file_performance',
      'view_employee_file_leave',
      'view_employee_file_injury',
      'view_employee_file_payroll',
      'view_employee_file_requests',
      'view_employee_file_agreement',
      'view_disciplinary_actions',
      'view_time_off',
    ],
  },
  {
    id: 'events',
    label: 'Audit events',
    description: 'Per-employee employment events',
    permission: 'view_events'
  },
  {
    id: 'roles_wages',
    label: 'Roles & wages',
    description: 'Assigned roles and pay rates',
    // Wages are sensitive — require wage permission (not roles-only)
    permission: 'view_wages'
  },
  {
    id: 'time_off',
    label: 'Time off',
    description: 'Recorded time-off blocks',
    permission: 'view_time_off'
  },
  {
    id: 'schedule_prefs',
    label: 'Schedule prefs',
    description: 'Hours targets and availability on file',
    permission: 'view_schedule_prefs'
  },
  {
    id: 'schedule_prefs_form',
    label: 'Schedule prefs form',
    description:
      'Blank worksheet, or send each person a fillable link (email or copy). They can type a reply and do not need an account',
    permission: 'view_schedule_prefs'
  }
];

export function categoryAllowed(category, hasPermission) {
  if (!category || typeof hasPermission !== 'function') return false;
  if (category.permission) return hasPermission(category.permission);
  if (Array.isArray(category.allPermissions) && category.allPermissions.length > 0) {
    return category.allPermissions.every((perm) => hasPermission(perm));
  }
  if (Array.isArray(category.anyPermissions) && category.anyPermissions.length > 0) {
    return category.anyPermissions.some((perm) => hasPermission(perm));
  }
  return false;
}

/** Categories the user is allowed to print. */
export function getAllowedPrintCategories(hasPermission) {
  return PRINT_CATEGORIES.filter((cat) => categoryAllowed(cat, hasPermission));
}

/**
 * Filter a set/list of category ids down to those the user may print.
 * @returns {Set<string>}
 */
export function filterAllowedPrintSections(sectionIds, hasPermission) {
  const allowed = new Set(
    getAllowedPrintCategories(hasPermission).map((cat) => cat.id)
  );
  const input = sectionIds instanceof Set ? sectionIds : new Set(sectionIds || []);
  return new Set([...input].filter((id) => allowed.has(id)));
}
